// Dossiers de subvention (D-112) : la logique sans écran, testée (subventions.test.ts).
//  - le calendrier : campagnes nationales EN EXEMPLE (dates indicatives) et campagnes locales du club, qui reviennent ;
//  - le coffre : l'état de chaque pièce (à jour, à renouveler, manquante) et son échéance ;
//  - les chiffres : compter ce qui est validé, remplacé, à vérifier ;
//  - la fiche action : 5 étapes, budget équilibré (charges = produits), indicateurs et leurs mesures ;
//  - les textes à coller et les aperçus CERFA 12156 (demande) et 15059 (bilan), assemblés à partir des réponses
//    du bureau et de modèles écrits à l'avance (pas d'IA).
import { euros } from './licences';
import { parseLocalDate, premier } from './schedule';

/** Un montant (centimes) : « 1 500 € », espaces insécables simples (l'espace fine de toLocaleString disparaît
 *  dans certaines polices et passe mal au copier-coller dans Le Compte Asso). */
export const eur = (cents: number) => euros(cents).replace(/\u202f/g, '\u00a0');

// --- Pièces du coffre ---------------------------------------------------------------------------
export type DocKind = 'statuts' | 'dirigeants' | 'rib' | 'siret_rna' | 'pv_ag' | 'comptes' | 'assurance' | 'budget' | 'rapport' | 'autre';
/** Les pièces que tout dossier demande, dans l'ordre du coffre. */
export const DOC_KINDS: Exclude<DocKind, 'autre'>[] = ['statuts', 'dirigeants', 'rib', 'siret_rna', 'pv_ag', 'comptes', 'assurance', 'budget', 'rapport'];
export const DOC_LABEL: Record<DocKind, string> = {
  statuts: 'Statuts', dirigeants: 'Liste des dirigeants', rib: 'RIB du club', siret_rna: 'SIRET et RNA', pv_ag: 'PV de la dernière AG',
  comptes: 'Comptes approuvés', assurance: 'Attestation d’assurance', budget: 'Budget prévisionnel', rapport: 'Rapport d’activité', autre: 'Pièce',
};
/** Le nom court d'une pièce demandée par une campagne (puces). */
export const PIECE_CHIP: Record<Exclude<DocKind, 'autre'>, string> = {
  statuts: 'Statuts', dirigeants: 'Dirigeants', rib: 'RIB', siret_rna: 'SIRET', pv_ag: 'PV d’AG', comptes: 'Comptes', assurance: 'Assurance', budget: 'Budget', rapport: 'Rapport',
};
/** Ce que dit la pièce quand rien n'est encore noté. */
const DOC_EMPTY: Record<DocKind, string> = {
  statuts: 'La dernière version signée', dirigeants: 'Le bureau élu, avec les fonctions', rib: 'Au nom de l’association',
  siret_rna: 'Les numéros de l’association', pv_ag: 'Procès-verbal signé', comptes: 'Bilan et compte de résultat, après l’AG',
  assurance: 'Responsabilité civile du club', budget: 'Voté à l’AG', rapport: 'Les actions de la saison passée', autre: 'Pièce libre',
};
export const FILE_MAX = 3 * 1024 * 1024;
export const FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;

export interface GrantDoc {
  id: string; kind: DocKind; title: string | null; note: string | null; doc_date: string | null; expires_on: string | null;
  held_outside: boolean; file_name: string | null; file_mime: string | null; file_size: number | null; file_at: string | null;
}
export interface GrantProfile {
  club_id: string; legal_name: string | null; acronym: string | null; siret: string | null; rna: string | null; address: string | null;
  president: string | null; affiliation: string | null; agrement: string | null; employees: number | null;
  budget_label: string | null; budget: Budget;
}
export type DocTone = 'ok' | 'warn' | 'ko';
export interface DocState { tone: DocTone; chip: string; meta: string; title: string; missing: boolean }

const DAY = 86400000;
export const daysBetween = (from: string, to: string) => Math.round((parseLocalDate(to).getTime() - parseLocalDate(from).getTime()) / DAY);
export const frDate = (iso: string, year = true) =>
  premier(parseLocalDate(iso).toLocaleDateString('fr-FR', year ? { day: 'numeric', month: 'short', year: 'numeric' } : { day: 'numeric', month: 'short' }));
export const frLong = (iso: string) => premier(parseLocalDate(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' }));

/** La saison FFBB d'une date (1er juillet → 30 juin, D-115) : « 2026-2027 ». */
export function seasonOf(iso: string): string {
  const [y, m] = iso.split('-').map(Number) as [number, number];
  const start = m < 7 ? y - 1 : y;
  return `${start}-${start + 1}`;
}
export const prevSeason = (s: string, n = 1) => { const y = Number(s.slice(0, 4)) - n; return `${y}-${y + 1}`; };
export const shortSeason = (s: string) => `${s.slice(2, 4)}-${s.slice(7, 9)}`;

export const formatSiret = (s: string) => s.replace(/\D/g, '').replace(/^(\d{3})(\d{3})(\d{3})(\d{5})$/, '$1 $2 $3 $4');
export const cleanSiret = (s: string) => s.replace(/\D/g, '');
export const cleanRna = (s: string) => s.replace(/\s/g, '').toUpperCase();
export const validSiret = (s: string) => /^\d{14}$/.test(cleanSiret(s));
export const validRna = (s: string) => /^W[0-9A-Z]{9}$/.test(cleanRna(s));

/** Le titre d'une pièce : « Comptes approuvés 2025-2026 » (la saison écoulée), « Budget prévisionnel 2026-2027 ». */
export function docTitle(kind: DocKind, doc: Pick<GrantDoc, 'title'> | undefined, today: string): string {
  if (kind === 'autre') return doc?.title ?? 'Pièce';
  if (kind === 'comptes') return `${DOC_LABEL.comptes} ${prevSeason(seasonOf(today))}`;
  if (kind === 'budget') return `${DOC_LABEL.budget} ${seasonOf(today)}`;
  return DOC_LABEL[kind];
}

/** L'état d'une pièce : manquante, expirée, à renouveler (échéance dans 90 jours ; PV d'AG quand la prochaine AG approche), à jour.
 *  SIRET et RNA : à jour dès que les deux numéros sont notés ; le budget : dès qu'il est saisi à la main. */
export function docState(kind: DocKind, doc: GrantDoc | undefined, today: string, opts: { nextAg?: string | null; profile?: GrantProfile | null } = {}): DocState {
  const title = docTitle(kind, doc, today);
  const p = opts.profile;
  const has = !!doc && (!!doc.file_name || doc.held_outside);
  const kept = doc?.held_outside && !doc.file_name ? ' · gardée au club' : '';
  if (kind === 'siret_rna' && p?.siret && p.rna && !has) return { tone: 'ok', chip: 'À jour', meta: `${formatSiret(p.siret)} · ${p.rna}`, title, missing: false };
  if (kind === 'budget' && !has && p && budgetTotals(p.budget).charges > 0) {
    return { tone: 'ok', chip: 'À jour', meta: `${p.budget_label ? `${p.budget_label} · ` : ''}saisi à la main, ${eur(budgetTotals(p.budget).charges)}`, title, missing: false };
  }
  if (!has) return { tone: 'ko', chip: 'Manquante', meta: doc?.note || DOC_EMPTY[kind], title, missing: true };
  let meta = doc!.note || docMeta(kind, doc!, p);
  meta += kept;
  if (doc!.expires_on) {
    const d = daysBetween(today, doc!.expires_on);
    if (d < 0) return { tone: 'ko', chip: 'Expirée', meta: `${meta} · expirée le ${frDate(doc!.expires_on)}`, title, missing: true };
    if (!doc!.note) meta = `${meta} · expire le ${frDate(doc!.expires_on)}`;
    if (d <= 90) return { tone: 'warn', chip: d === 0 ? 'expire aujourd’hui' : `dans ${d} jour${d > 1 ? 's' : ''}`, meta, title, missing: false };
  }
  if (kind === 'pv_ag' && opts.nextAg && (!doc!.doc_date || doc!.doc_date < opts.nextAg) && daysBetween(today, opts.nextAg) <= 60) {
    return { tone: 'warn', chip: 'à renouveler', meta: `${meta} · à remplacer après celle du ${frDate(opts.nextAg, false)}`, title, missing: false };
  }
  return { tone: 'ok', chip: 'À jour', meta, title, missing: false };
}

function docMeta(kind: DocKind, doc: GrantDoc, p?: GrantProfile | null): string {
  const d = doc.doc_date ? frDate(doc.doc_date) : null;
  switch (kind) {
    case 'statuts': return d ? `Version signée du ${d}` : DOC_EMPTY.statuts;
    case 'dirigeants': return d ? `Bureau élu le ${d}` : DOC_EMPTY.dirigeants;
    case 'pv_ag': return d ? `AG du ${d}` : DOC_EMPTY.pv_ag;
    case 'siret_rna': return p?.siret || p?.rna ? [p.siret ? formatSiret(p.siret) : null, p.rna].filter(Boolean).join(' · ') : 'Avis de situation, récépissé';
    case 'assurance': return DOC_EMPTY.assurance;
    default: {
      const when = doc.file_at ? frDate(doc.file_at.slice(0, 10)) : d;
      return when ? `Déposé le ${when}` : DOC_EMPTY[kind];
    }
  }
}

/** Les pièces dans l'ordre du coffre : manquantes, à renouveler, puis à jour. */
export function vaultRows(docs: GrantDoc[], today: string, opts: { nextAg?: string | null; profile?: GrantProfile | null } = {}) {
  const rows = [
    ...DOC_KINDS.map(k => ({ kind: k as DocKind, doc: docs.find(d => d.kind === k) })),
    ...docs.filter(d => d.kind === 'autre').map(d => ({ kind: 'autre' as DocKind, doc: d })),
  ].map((r, i) => ({ ...r, state: docState(r.kind, r.doc, today, opts), i }));
  const rank: Record<DocTone, number> = { ko: 0, warn: 1, ok: 2 };
  return rows.sort((a, b) => rank[a.state.tone] - rank[b.state.tone] || a.i - b.i);
}
export function vaultSummary(rows: { state: DocState }[]) {
  return { ok: rows.filter(r => r.state.tone === 'ok').length, warn: rows.filter(r => r.state.tone === 'warn').length, ko: rows.filter(r => r.state.tone === 'ko').length };
}
/** Les pièces d'une campagne prêtes : « 7/9 pièces ». */
export function piecesReady(pieces: string[], rows: { kind: DocKind; state: DocState }[]): { ready: number; total: number } {
  const want = pieces.length ? pieces : DOC_KINDS;
  return { ready: want.filter(k => rows.some(r => r.kind === k && r.state.tone !== 'ko')).length, total: want.length };
}

// --- Le calendrier ---------------------------------------------------------------------------------
export type FunderKind = 'etat' | 'commune' | 'departement' | 'region' | 'fondation' | 'autre';
export const FUNDER_KINDS: Exclude<FunderKind, 'etat'>[] = ['commune', 'departement', 'region', 'fondation', 'autre'];
export const FUNDER_LABEL: Record<FunderKind, string> = { etat: 'État', commune: 'Commune', departement: 'Département', region: 'Région', fondation: 'Fondation', autre: 'Autre' };

export interface National {
  key: string; funder: string; program: string; via: string | null; window_label: string;
  month_from: number | null; month_to: number | null; info_url: string | null; pieces: string[]; position: number;
}
export interface Campaign {
  id: string; national_key: string | null; funder_kind: FunderKind; funder: string | null; name: string | null; deadline: string | null;
  link: string | null; remind: boolean; yearly: boolean; pieces: string[]; note: string | null; dismissed: boolean;
  reminded_j30: string | null; reminded_j7: string | null; created_at: string;
}
export type AppStatus = 'brouillon' | 'pret' | 'depose' | 'accorde' | 'refuse';
export interface Application {
  id: string; campaign_id: string | null; national_key: string | null; funder: string; program: string | null; year: number; season: string;
  status: AppStatus; amount_requested_cents: number | null; amount_granted_cents: number | null;
  action: ActionData; budget: Budget; indicators: Indicator[]; bilan: Bilan; deposited_on: string | null; bilan_done_at: string | null;
  created_at: string; updated_at: string;
}

/** La prochaine date limite d'une campagne : la sienne si elle est à venir ; passée et « chaque année », l'an
 *  suivant, estimée (à confirmer) ; sinon aucune. */
export function nextDeadline(c: Pick<Campaign, 'deadline' | 'yearly'>, today: string): { date: string; confirmed: boolean } | null {
  if (!c.deadline) return null;
  if (c.deadline >= today) return { date: c.deadline, confirmed: true };
  if (!c.yearly) return null;
  let [y] = c.deadline.split('-').map(Number) as [number];
  const md = c.deadline.slice(4);
  let d = `${y}${md}`;
  while (d < today) { y++; d = `${y}${md}`; }
  if (md === '-02-29' && !(y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0))) d = `${y}-02-28`;
  return { date: d, confirmed: false };
}

const MONTHS = ['Janv.', 'Févr.', 'Mars', 'Avril', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'];
const monthsLabel = (a: number, b: number) => a === b ? MONTHS[a - 1]! : `${MONTHS[a - 1]!}–${MONTHS[b - 1]!.toLowerCase()}`;

export interface CalEntry {
  key: string;                       // « n:ans_psf » ou l'id de la campagne locale
  national?: National; campaign?: Campaign;
  title: string; line: string;
  date: string | null;               // date limite (confirmée ou estimée)
  confirmed: boolean;                // date de l'année confirmée par le bureau
  example: boolean;                  // campagne nationale non datée : dates en exemple
  sort: string;                      // pour ranger
  month: string; year: string;       // « Nov. » « 2026 », « Janv.–mars » « 2027 », « Toute l’année » « »
  chip: { label: string; tone: 'todo' | 'soft' | 'ok' | 'ct' | 'warn' };
  remind?: string;                   // « ⏰ Rappels J-30 ✓ · J-7 »
  pieces: string[];
  app?: Application;                 // le dossier de l'année
  bilanDue?: Application;            // un dossier accordé dont le bilan reste à rendre
}

const appChip = (a: Application): CalEntry['chip'] =>
  a.status === 'depose' ? { label: 'Déposé', tone: 'ct' } : a.status === 'accorde' ? { label: 'Accordé', tone: 'ok' }
  : a.status === 'refuse' ? { label: 'Refusé', tone: 'soft' } : a.status === 'pret' ? { label: 'Prêt à déposer', tone: 'ok' } : { label: 'En cours', tone: 'todo' };

export function campaignTitle(c: Pick<Campaign, 'national_key' | 'funder' | 'name'>, nationals: National[]): string {
  const n = c.national_key ? nationals.find(x => x.key === c.national_key) : null;
  return n ? `${n.funder} · ${n.program}` : c.funder ?? c.name ?? 'Campagne';
}

/** Le calendrier : campagnes du club et nationales en exemple, rangées par date (confirmée, estimée ou période). */
export function calendar(nationals: National[], campaigns: Campaign[], apps: Application[], today: string): CalEntry[] {
  const out: CalEntry[] = [];
  const [ty, tm] = today.split('-').map(Number) as [number, number];
  for (const n of [...nationals].sort((a, b) => a.position - b.position)) {
    const c = campaigns.find(x => x.national_key === n.key);
    if (c?.dismissed) continue;
    const nd = c ? nextDeadline({ deadline: c.deadline, yearly: false }, today) : null;
    const bilanDue = apps.filter(a => a.national_key === n.key && a.status === 'accorde' && !a.bilan_done_at).sort((a, b) => b.year - a.year)[0];
    const lastGrant = apps.filter(a => a.national_key === n.key && a.status === 'accorde').sort((a, b) => b.year - a.year)[0];
    let year: number | null = null, month = 'Toute l’année', sort = '9999-12-31';
    if (nd) { year = Number(nd.date.slice(0, 4)); month = MONTHS[Number(nd.date.slice(5, 7)) - 1]!; sort = nd.date; }
    else if (n.month_from && n.month_to) {
      year = tm > n.month_to ? ty + 1 : ty;
      month = monthsLabel(n.month_from, n.month_to);
      sort = `${year}-${String(n.month_from).padStart(2, '0')}-01`;
    }
    const app = year ? apps.find(a => a.national_key === n.key && a.year === year) : undefined;
    const via = n.via ? `${n.via}, ` : '';
    const line = nd ? `${n.via ? `${n.via} · ` : ''}date limite le ${frLong(nd.date)} · confirmée par le bureau`
      : n.month_from ? `${via}${n.window_label} · date à confirmer` : `${n.via ?? 'Toute l’année'} · date à confirmer`;
    const grant = lastGrant?.amount_granted_cents ? ` · ${lastGrant.year} : ${eur(lastGrant.amount_granted_cents)} accordés` : '';
    out.push({
      key: `n:${n.key}`, national: n, campaign: c, title: `${n.funder} · ${n.program}`, line: line + grant,
      date: nd?.date ?? null, confirmed: !!nd, example: !nd, sort, month, year: year ? String(year) : '',
      chip: bilanDue ? { label: 'Bilan à rendre', tone: 'ct' } : app ? appChip(app)
        : nd ? { label: 'À préparer', tone: 'todo' } : n.month_from ? { label: 'rappel à l’ouverture', tone: 'soft' } : { label: 'à surveiller', tone: 'soft' },
      remind: nd && c?.remind ? remindLine(c, nd.date) : undefined, pieces: c?.pieces.length ? c.pieces : n.pieces, app, bilanDue,
    });
  }
  for (const c of campaigns.filter(x => !x.national_key)) {
    const nd = nextDeadline(c, today);
    if (!nd) continue;
    const year = Number(nd.date.slice(0, 4));
    const app = apps.find(a => a.campaign_id === c.id && a.year === year);
    out.push({
      key: c.id, campaign: c, title: c.funder ?? 'Campagne',
      line: `${c.name ?? ''} · ${nd.confirmed ? `avant le ${frDate(nd.date, false)}` : `vers le ${frDate(nd.date, false)}, à confirmer`} · ajoutée par le bureau`,
      date: nd.date, confirmed: nd.confirmed, example: false, sort: nd.date, month: MONTHS[Number(nd.date.slice(5, 7)) - 1]!, year: String(year),
      chip: app ? appChip(app) : nd.confirmed ? { label: 'À préparer', tone: 'todo' } : { label: 'date à confirmer', tone: 'soft' },
      remind: c.remind && nd.confirmed ? remindLine(c, nd.date) : undefined, pieces: c.pieces, app,
    });
  }
  return out.sort((a, b) => a.sort.localeCompare(b.sort) || a.title.localeCompare(b.title));
}
const remindLine = (c: Pick<Campaign, 'reminded_j30' | 'reminded_j7'>, date: string) =>
  `⏰ Rappels J-30${c.reminded_j30 === date ? ' ✓' : ''} · J-7${c.reminded_j7 === date ? ' ✓' : ''}`;

/** La prochaine échéance confirmée (le grand encart) : la plus proche, à venir. */
export const nextDue = (entries: CalEntry[], today: string) => entries.filter(e => e.confirmed && e.date && e.date >= today).sort((a, b) => a.date!.localeCompare(b.date!))[0] ?? null;

/** Les mois du calendrier, dans l'ordre. */
export function calendarMonths(entries: CalEntry[]): { month: string; year: string; example: boolean; items: CalEntry[] }[] {
  const out: { month: string; year: string; example: boolean; items: CalEntry[] }[] = [];
  for (const e of entries) {
    const last = out[out.length - 1];
    if (last && last.month === e.month && last.year === e.year) { last.items.push(e); last.example ||= e.example && !!e.national?.month_from; }
    else out.push({ month: e.month, year: e.year, example: e.example && !!e.national?.month_from, items: [e] });
  }
  return out;
}

// --- Les chiffres ------------------------------------------------------------------------------------
export type FigureKey = 'licencies' | 'licenciees' | 'moins18' | 'benevoles' | 'heures' | 'missions' | 'km' | 'actions' | 'partenaires' | 'apports'
  | 'lic_f_m18' | 'lic_g_m18' | 'lic_f_18' | 'lic_h_18';
export type FigureState = 'replaced' | 'confirmed' | 'proposed' | 'missing';
export interface Figure {
  key: FigureKey; label: string; unit: string; season: string; value: number | null; source: string | null; scope: string | null; state: FigureState;
  entered_at: string | null; entered_by: string | null; note: string | null; shared: boolean;
  arceau_value: number | null; arceau_source: string | null; arceau_scope: string | null; arceau_was: number | null; arceau_changed: boolean;
}
/** Les chiffres en cartes (la saison en cours) ; le tableau âge × sexe sur 3 saisons. */
export const CARD_KEYS: FigureKey[] = ['licencies', 'licenciees', 'moins18', 'benevoles', 'heures', 'missions', 'km', 'actions', 'partenaires', 'apports'];
export const TABLE_KEYS: FigureKey[] = ['lic_f_m18', 'lic_g_m18', 'lic_f_18', 'lic_h_18'];

export const num = (n: number) => n.toLocaleString('fr-FR', { maximumFractionDigits: 1 });
export function fmtFigure(value: number | null, unit = ''): string {
  if (value === null || value === undefined) return '—';
  if (unit === '€') return eur(Math.round(value * 100));
  return `${num(value)}${unit ? ` ${unit}` : ''}`;
}
export function figureCounts(figs: Figure[]) {
  const cards = figs.filter(f => CARD_KEYS.includes(f.key));
  return {
    total: cards.length, confirmed: cards.filter(f => f.state === 'confirmed').length, replaced: cards.filter(f => f.state === 'replaced').length,
    todo: cards.filter(f => f.state === 'proposed' || f.state === 'missing').length,
  };
}
/** Le total d'une colonne du tableau : « 108 + ? » quand une case manque. */
export function tableTotal(figs: (Figure | undefined)[]): string {
  const sum = figs.reduce((s, f) => s + (f?.value ?? 0), 0);
  const missing = figs.some(f => !f || f.value === null || f.value === undefined);
  return `${num(sum)}${missing ? ' + ?' : ''}`;
}
/** Le tableau âge × sexe est-il complet (toutes les cases saisies ou validées) ? */
export const tableComplete = (figs: (Figure | undefined)[]) => figs.every(f => !!f && (f.state === 'replaced' || f.state === 'confirmed'));

// --- La fiche action ------------------------------------------------------------------------------
export interface ActionData {
  model?: string;
  title?: string; objective?: string; need?: string;
  public?: { sex?: 'F' | 'M' | 'X'; age_min?: number | null; age_max?: number | null; count?: number | null; place?: string; text?: string };
  means?: { what?: string; staff?: string; dates?: string; place?: string; partners?: string };
}
export interface BudgetLine { l: string; c: number; r?: string; ask?: boolean; real?: number | null }
export interface Budget { charges: BudgetLine[]; produits: BudgetLine[]; benevolat?: number; heures?: number | null; real_benevolat?: number | null }
export const STEPS = ['Objectif', 'Public', 'Moyens', 'Budget', 'Indicateurs'] as const;

export const sumLines = (lines: BudgetLine[] | undefined) => (lines ?? []).reduce((s, l) => s + (Number.isFinite(l.c) ? l.c : 0), 0);
export function budgetTotals(b: Budget | null | undefined) {
  const v = b?.benevolat ?? 0;
  const charges = sumLines(b?.charges) + v, produits = sumLines(b?.produits) + v;
  return { charges, produits, gap: produits - charges, balanced: charges > 0 && charges === produits, ask: (b?.produits ?? []).find(l => l.ask)?.c ?? 0 };
}
export function balanceLine(b: Budget): { ok: boolean; text: string } {
  const t = budgetTotals(b);
  if (t.charges === 0) return { ok: false, text: 'Ajoute les charges de l’action, puis ses produits.' };
  if (t.balanced) return { ok: true, text: `✓ Budget équilibré : ${eur(t.charges)} = ${eur(t.produits)}` };
  return { ok: false, text: t.gap < 0 ? `Il manque ${eur(-t.gap)} de produits pour équilibrer (${eur(t.charges)} de charges).`
    : `${eur(t.gap)} de produits en trop : ajoute des charges ou baisse un produit.` };
}
/** Les rubriques du budget (comptes du plan associatif, comme sur les CERFA). */
export const CHARGE_RUBRICS: Record<string, string> = {
  '60': 'Achats (matériel, fournitures)', '61': 'Services extérieurs (locations, assurances)', '62': 'Autres services extérieurs (déplacements, communication, honoraires)',
  '63': 'Impôts et taxes', '64': 'Charges de personnel', '65': 'Autres charges de gestion courante', '68': 'Dotations aux amortissements',
};
export const PRODUCT_RUBRICS: Record<string, string> = {
  '70': 'Ventes, prestations (buvette, entrées, stages)', '74': 'Subventions (État, région, département, commune…)', '75': 'Cotisations, dons, mécénat, sponsoring',
  '76': 'Produits financiers', '77': 'Produits exceptionnels', '78': 'Reprises, fonds propres affectés',
};

export function stepDone(app: Pick<Application, 'action' | 'budget' | 'indicators'>, i: number): boolean {
  const a = app.action ?? {};
  if (i === 0) return !!a.title?.trim() && !!a.objective?.trim();
  if (i === 1) return !!a.public && !!(a.public.count && a.public.count > 0) && !!(a.public.age_min || a.public.age_max || a.public.text?.trim());
  if (i === 2) return !!a.means?.what?.trim();
  if (i === 3) return budgetTotals(app.budget).balanced && budgetTotals(app.budget).ask > 0;
  return (app.indicators ?? []).some(x => x.label?.trim() && x.target !== null && x.target !== undefined);
}
export const allDone = (app: Pick<Application, 'action' | 'budget' | 'indicators'>) => STEPS.every((_, i) => stepDone(app, i));
export const firstTodo = (app: Pick<Application, 'action' | 'budget' | 'indicators'>) => { const i = STEPS.findIndex((_, k) => !stepDone(app, k)); return i < 0 ? 4 : i; };

export function publicLine(p: ActionData['public']): string {
  if (!p) return '';
  const who = p.sex === 'F' ? 'filles' : p.sex === 'M' ? 'garçons' : 'filles et garçons';
  const ages = p.age_min && p.age_max ? ` de ${p.age_min} à ${p.age_max} ans` : p.age_min ? ` à partir de ${p.age_min} ans` : p.age_max ? ` jusqu’à ${p.age_max} ans` : '';
  const parts = [`${who.charAt(0).toUpperCase()}${who.slice(1)}${ages}`];
  if (p.count) parts.push(`${p.count} bénéficiaire${p.count > 1 ? 's' : ''} visé${p.count > 1 ? 's' : ''}`);
  if (p.place?.trim()) parts.push(p.place.trim());
  if (p.text?.trim()) parts.push(p.text.trim());
  return parts.join(' · ');
}

// --- Indicateurs et leurs mesures -----------------------------------------------------------------------
export type MeasureKind = 'fig' | 'lic' | 'agenda' | 'inscrits' | 'manual';
export interface Indicator {
  label: string; target: number | null; m: MeasureKind; key?: FigureKey; sex?: 'F' | 'M' | null; under?: number | null; match?: string | null;
  real?: number | null;        // réalisé saisi par le bureau (bilan)
}
export interface Measure { value: number | null; source: string | null; scope: string | null }
/** Les mesures proposées, avec ce qu'Arceau compte. */
export const MEASURES: { id: string; label: string; ind: Omit<Indicator, 'label' | 'target'> }[] = [
  { id: 'lic', label: 'Licenciés (sexe, âge au choix)', ind: { m: 'lic' } },
  { id: 'fig:licencies', label: 'Licenciés du club (chiffre retenu)', ind: { m: 'fig', key: 'licencies' } },
  { id: 'fig:licenciees', label: 'Licenciées, filles et femmes (chiffre retenu)', ind: { m: 'fig', key: 'licenciees' } },
  { id: 'fig:benevoles', label: 'Bénévoles actifs', ind: { m: 'fig', key: 'benevoles' } },
  { id: 'fig:heures', label: 'Heures de bénévolat', ind: { m: 'fig', key: 'heures' } },
  { id: 'agenda', label: 'Événements de l’agenda (mot du titre)', ind: { m: 'agenda' } },
  { id: 'inscrits', label: 'Inscrits à ces événements', ind: { m: 'inscrits' } },
  { id: 'manual', label: 'Saisi par le bureau', ind: { m: 'manual' } },
];
export const measureId = (i: Pick<Indicator, 'm' | 'key'>) => (i.m === 'fig' ? `fig:${i.key}` : i.m);
export const measureSpec = (i: Indicator) => ({ m: i.m, key: i.key, sex: i.sex ?? undefined, under: i.under ?? undefined, match: i.match ?? undefined });
/** La source d'un réalisé : le bureau s'il l'a saisi, sinon ce qu'Arceau mesure. */
export function realized(i: Indicator, m: Measure | undefined): { value: number | null; by: 'bureau' | 'arceau' | null; source: string; arceau: number | null } {
  const arceau = m?.value ?? null;
  if (i.real !== null && i.real !== undefined) return { value: i.real, by: 'bureau', source: 'bureau', arceau: i.m === 'manual' ? null : arceau };
  if (i.m === 'manual' || arceau === null) return { value: null, by: null, source: 'à saisir', arceau: null };
  return { value: arceau, by: 'arceau', source: m?.source === 'bureau' ? 'bureau' : m?.source ?? 'Arceau', arceau };
}
export const reached = (i: Indicator, v: number | null) => v !== null && i.target !== null && v >= i.target;

// --- Bilan ----------------------------------------------------------------------------------------------
export interface Bilan { done?: string; public?: string; dates?: string; why?: Record<string, string>; quali?: string }
/** Les lignes de charges dont l'écart prévu / réalisé dépasse 20 % : une phrase pour l'expliquer. */
export function bilanGaps(b: Budget): { l: string; planned: number; real: number; pct: number }[] {
  return (b.charges ?? []).filter(l => l.real !== null && l.real !== undefined && l.c > 0)
    .map(l => ({ l: l.l, planned: l.c, real: l.real!, pct: Math.round(((l.real! - l.c) / l.c) * 100) }))
    .filter(g => Math.abs(g.pct) > 20);
}
export function bilanTotals(b: Budget) {
  const planned = budgetTotals(b).charges;
  const real = (b.charges ?? []).reduce((s, l) => s + (l.real ?? l.c), 0) + (b.real_benevolat ?? b.benevolat ?? 0);
  return { planned, real, pct: planned ? Math.round(((real - planned) / planned) * 100) : 0 };
}

// --- Modèles de rédaction (écrits à l'avance, sans IA) ---------------------------------------------------
export interface Model { id: string; label: string; for: string; action: ActionData; charges: BudgetLine[]; indicators: Indicator[] }
export function models(club: string, gym: string): Model[] {
  const at = gym ? ` au ${gym}` : '';
  return [
    { id: 'filles', label: 'Plus de filles au basket', for: 'Agence nationale du Sport, commune',
      action: { model: 'filles', title: 'Plus de filles au basket', objective: `Faire découvrir le basket aux filles du quartier et les garder au club : plus de licenciées chez les jeunes en une saison.`,
        need: `Les filles sont encore peu nombreuses chez les jeunes du ${club}, surtout avant 13 ans.`,
        public: { sex: 'F', age_min: 5, age_max: 13 }, means: { what: `Des séances découverte encadrées par des coachs du club${at}, avec les écoles du quartier, et une journée portes ouvertes.` } },
      charges: [{ l: 'Encadrement', c: 0, r: '64' }, { l: 'Matériel', c: 0, r: '60' }, { l: 'Déplacements', c: 0, r: '62' }, { l: 'Communication', c: 0, r: '62' }],
      indicators: [{ label: 'Licenciées de moins de 13 ans', target: null, m: 'lic', sex: 'F', under: 13 }, { label: 'Séances découverte', target: null, m: 'agenda', match: 'découverte' }] },
    { id: 'quartier', label: 'Le basket pour les jeunes du quartier', for: 'Agence nationale du Sport, département',
      action: { model: 'quartier', title: 'Le basket pour les jeunes du quartier', objective: 'Rendre le basket accessible aux jeunes qui n’en font pas, près de chez eux, à petit prix.',
        need: 'Peu de jeunes du quartier pratiquent un sport en club : le coût et la distance les en éloignent.',
        public: { sex: 'X', age_min: 8, age_max: 17 }, means: { what: `Des créneaux ouverts le mercredi et pendant les vacances${at}, licence à prix réduit, prêt de matériel.` } },
      charges: [{ l: 'Encadrement', c: 0, r: '64' }, { l: 'Matériel', c: 0, r: '60' }, { l: 'Location de salle', c: 0, r: '61' }],
      indicators: [{ label: 'Jeunes accueillis', target: null, m: 'manual' }, { label: 'Licenciés de moins de 18 ans', target: null, m: 'lic', under: 18 }] },
    { id: 'formation', label: 'Former nos bénévoles', for: 'FDVA 1',
      action: { model: 'formation', title: 'Former nos bénévoles', objective: 'Former les bénévoles du club à l’arbitrage, à la table de marque et à l’encadrement des jeunes.',
        need: 'Le club manque de bénévoles formés pour tenir les matchs du week-end.',
        public: { sex: 'X', age_min: 16, text: 'bénévoles et parents du club' }, means: { what: 'Des sessions de formation avec le comité départemental, et un tutorat par les bénévoles expérimentés.' } },
      charges: [{ l: 'Formation', c: 0, r: '62' }, { l: 'Déplacements', c: 0, r: '62' }, { l: 'Supports', c: 0, r: '60' }],
      indicators: [{ label: 'Bénévoles formés', target: null, m: 'manual' }, { label: 'Bénévoles actifs', target: null, m: 'fig', key: 'benevoles' }] },
    { id: 'fonctionnement', label: 'Le fonctionnement du club', for: 'FDVA 2, commune',
      action: { model: 'fonctionnement', title: `Le fonctionnement du ${club}`, objective: 'Permettre au club d’accueillir tous ses licenciés dans de bonnes conditions toute la saison.',
        need: 'Les frais d’arbitrage, d’engagement et de déplacement augmentent chaque saison.',
        public: { sex: 'X', text: 'tous les licenciés du club' }, means: { what: `Entraînements et matchs de toutes les équipes${at}, encadrés par des coachs bénévoles.` } },
      charges: [{ l: 'Engagements et licences', c: 0, r: '65' }, { l: 'Arbitrage', c: 0, r: '62' }, { l: 'Déplacements', c: 0, r: '62' }, { l: 'Matériel', c: 0, r: '60' }],
      indicators: [{ label: 'Licenciés', target: null, m: 'fig', key: 'licencies' }, { label: 'Heures de bénévolat', target: null, m: 'fig', key: 'heures' }] },
    { id: 'emploi', label: 'Un emploi d’éducateur sportif', for: 'Agence nationale du Sport · emploi',
      action: { model: 'emploi', title: 'Un éducateur sportif pour le club', objective: 'Créer ou pérenniser un poste d’éducateur diplômé pour encadrer les jeunes et former les coachs bénévoles.',
        need: 'Les coachs bénévoles ne suffisent plus à encadrer toutes les équipes de jeunes.',
        public: { sex: 'X', age_min: 5, age_max: 18 }, means: { what: 'Un éducateur diplômé, à temps partiel, qui encadre les jeunes et accompagne les coachs bénévoles.' } },
      charges: [{ l: 'Salaire et charges', c: 0, r: '64' }, { l: 'Formation', c: 0, r: '62' }, { l: 'Déplacements', c: 0, r: '62' }],
      indicators: [{ label: 'Heures d’encadrement par semaine', target: null, m: 'manual' }, { label: 'Licenciés de moins de 18 ans', target: null, m: 'lic', under: 18 }] },
  ];
}

// --- Le texte prêt à coller dans Le Compte Asso ------------------------------------------------------------
export interface PasteField { label: string; text: string }
export function pasteFields(app: Application, figs: Figure[], clubName: string): PasteField[] {
  const a = app.action ?? {};
  const t = budgetTotals(app.budget);
  const f = (k: FigureKey) => figs.find(x => x.key === k);
  const others = (app.budget.produits ?? []).filter(l => !l.ask && l.c > 0).map(l => `${l.l} ${eur(l.c)}`);
  const ind = (app.indicators ?? []).filter(i => i.label?.trim()).map(i => `${i.label}${i.target !== null && i.target !== undefined ? ` (objectif : ${num(i.target)})` : ''}`);
  const desc = [a.means?.what, a.means?.staff ? `Encadrement : ${a.means.staff}.` : '', a.means?.place ? `Lieu : ${a.means.place}.` : '', a.means?.dates ? `Dates : ${a.means.dates}.` : '',
    a.means?.partners ? `Avec : ${a.means.partners}.` : ''].filter(x => x && x.trim()).map(x => x!.trim().replace(/([^.!?])$/, '$1.')).join(' ');
  const lic = f('licencies'), ben = f('benevoles');
  const human = [lic?.value !== null && lic?.value !== undefined ? `${num(lic.value)} licenciés` : '', ben?.value ? `${num(ben.value)} bénévoles actifs` : ''].filter(Boolean).join(' et ');
  return [
    { label: 'Intitulé de l’action', text: a.title?.trim() ?? '' },
    { label: 'Objectifs', text: a.objective?.trim() ?? '' },
    ...(a.need?.trim() ? [{ label: 'Besoins constatés', text: a.need.trim() }] : []),
    { label: 'Description', text: desc },
    { label: 'Public bénéficiaire', text: publicLine(a.public) },
    { label: 'Indicateurs', text: ind.join(' · ') },
    { label: 'Budget de l’action', text: `Budget total : ${eur(t.charges)}${app.budget.benevolat ? `, dont ${eur(app.budget.benevolat)} de bénévolat valorisé` : ''}. Montant demandé : ${eur(t.ask)}.${others.length ? ` Autres financements : ${others.join(', ')}.` : ''}` },
    ...(human ? [{ label: 'Moyens humains du club', text: `Le ${clubName} compte ${human} (saison ${app.season}).` }] : []),
  ].filter(x => x.text);
}

// --- Les aperçus CERFA : les rubriques du formulaire officiel, à reporter dessus -----------------------------
export interface CerfaRow { label: string; value: string | null; note?: string }
export interface CerfaSection { n: string; title: string; rows: CerfaRow[]; lines?: { title: string; rows: CerfaRow[] }[] }

const rubricRows = (lines: BudgetLine[], rubrics: Record<string, string>, real = false): CerfaRow[] => {
  const by = new Map<string, number>();
  for (const l of lines) { const k = l.r && rubrics[l.r] ? l.r : '?'; by.set(k, (by.get(k) ?? 0) + (real ? (l.real ?? l.c) : l.c)); }
  return [...by.entries()].sort().map(([k, c]) => ({ label: k === '?' ? 'Autres' : `${k} · ${rubrics[k]}`, value: eur(c) }));
};

/** CERFA 12156 (demande de subvention), rubriques dans l'ordre du formulaire. */
export function cerfa12156(app: Application, p: GrantProfile | null, figs: Figure[], clubName: string): { sections: CerfaSection[]; filled: number; missing: number } {
  const f = (k: FigureKey) => figs.find(x => x.key === k);
  const fig = (k: FigureKey): CerfaRow => { const x = f(k); return { label: '', value: x?.value !== null && x?.value !== undefined ? num(x.value) : null, note: x?.state === 'replaced' ? 'saisi par le bureau' : undefined }; };
  const a = app.action ?? {};
  const t = budgetTotals(app.budget);
  const pt = p ? budgetTotals(p.budget) : null;
  const v = (s: string | null | undefined) => (s && String(s).trim() ? String(s).trim() : null);
  const sections: CerfaSection[] = [
    { n: '1', title: 'Identification de l’association', rows: [
      { label: 'Nom', value: v(p?.legal_name) ?? clubName }, { label: 'Sigle', value: v(p?.acronym) },
      { label: 'N° RNA', value: v(p?.rna) }, { label: 'N° SIRET', value: p?.siret ? formatSiret(p.siret) : null },
      { label: 'Siège', value: v(p?.address) }, { label: 'Représentant légal', value: v(p?.president) }] },
    { n: '2', title: 'Relations avec l’administration', rows: [{ label: 'Agrément', value: v(p?.agrement) }] },
    { n: '3', title: 'Relations avec d’autres associations', rows: [{ label: 'Affiliation', value: v(p?.affiliation) ?? 'Fédération française de basketball' }] },
    { n: '4', title: 'Moyens humains', rows: [
      { ...fig('licencies'), label: 'Adhérents' }, { ...fig('benevoles'), label: 'Bénévoles' },
      { label: 'Salariés (ETP)', value: p?.employees !== null && p?.employees !== undefined ? num(p.employees) : null }] },
    { n: '5', title: `Budget de l’association${p?.budget_label ? ` · ${p.budget_label}` : ''}`, rows: pt && pt.charges > 0
      ? [{ label: 'Total des charges', value: eur(pt.charges) }, { label: 'Total des produits', value: eur(pt.produits) }]
      : [{ label: 'Budget prévisionnel', value: null, note: 'à saisir dans le coffre' }] },
    { n: '6', title: 'Projet · objet de la demande', rows: [
      { label: 'Intitulé', value: v(a.title) }, { label: 'Objectifs', value: v(a.objective) }, { label: 'Description', value: v(a.means?.what) },
      { label: 'Public', value: v(publicLine(a.public)) }, { label: 'Dates, lieu', value: v([a.means?.dates, a.means?.place].filter(Boolean).join(' · ')) },
      { label: 'Indicateurs', value: v((app.indicators ?? []).map(i => `${i.label}${i.target !== null ? ` : ${num(i.target)}` : ''}`).join(' · ')) },
      { label: 'Montant demandé', value: t.ask ? eur(t.ask) : null }, { label: 'Budget du projet', value: t.charges ? eur(t.charges) : null }],
      lines: [{ title: 'Charges du projet', rows: [...rubricRows(app.budget.charges ?? [], CHARGE_RUBRICS),
        ...(app.budget.benevolat ? [{ label: '86 · Contributions volontaires (bénévolat)', value: eur(app.budget.benevolat) }] : [])] },
      { title: 'Produits du projet', rows: [...(app.budget.produits ?? []).map(l => ({ label: `${l.r ? `${l.r} · ` : ''}${l.l}`, value: eur(l.c) })),
        ...(app.budget.benevolat ? [{ label: '87 · Contributions volontaires (bénévolat)', value: eur(app.budget.benevolat) }] : [])] }] },
    { n: '7', title: 'Attestations', rows: [{ label: 'Signature', value: null, note: `à signer par ${v(p?.president) ?? 'le représentant légal'}` }] },
  ];
  return { sections, ...countRows(sections) };
}

/** CERFA 15059 (compte rendu financier de l'action), prérempli des indicateurs prévus et réalisés. */
export function cerfa15059(app: Application, p: GrantProfile | null, measures: Measure[], clubName: string): { sections: CerfaSection[]; filled: number; missing: number } {
  const a = app.action ?? {};
  const bt = bilanTotals(app.budget);
  const v = (s: string | null | undefined) => (s && String(s).trim() ? String(s).trim() : null);
  const sections: CerfaSection[] = [
    { n: '1', title: 'Identification', rows: [
      { label: 'Association', value: v(p?.legal_name) ?? clubName }, { label: 'N° SIRET', value: p?.siret ? formatSiret(p.siret) : null },
      { label: 'Financeur', value: `${app.funder}${app.program ? ` · ${app.program}` : ''} · ${app.year}` },
      { label: 'Montant accordé', value: app.amount_granted_cents ? eur(app.amount_granted_cents) : null }] },
    { n: '2', title: 'Bilan qualitatif de l’action', rows: [
      { label: 'Action', value: v(a.title) }, { label: 'Réalisation', value: v(app.bilan?.done) },
      { label: 'Public touché', value: v(app.bilan?.public) ?? v(publicLine(a.public)) }, { label: 'Dates, lieux', value: v(app.bilan?.dates) ?? v([a.means?.dates, a.means?.place].filter(Boolean).join(' · ')) }],
      lines: [{ title: 'Indicateurs · prévu → réalisé', rows: (app.indicators ?? []).map((i, k) => {
        const r = realized(i, measures[k]);
        return { label: i.label, value: r.value !== null ? `${i.target !== null ? num(i.target) : '—'} → ${num(r.value)}` : null, note: r.value !== null ? `source : ${r.source}` : undefined };
      }) }] },
    { n: '3', title: 'Tableau de synthèse · charges', rows: [{ label: 'Prévu', value: eur(bt.planned) }, { label: 'Réalisé', value: eur(bt.real) }, { label: 'Écart', value: `${bt.pct > 0 ? '+' : ''}${bt.pct} %` }],
      lines: [{ title: 'Par ligne · prévu → réalisé', rows: (app.budget.charges ?? []).map(l => ({ label: l.l, value: `${eur(l.c)} → ${eur(l.real ?? l.c)}` })) }] },
    { n: '4', title: 'Explication des écarts', rows: bilanGaps(app.budget).map(g => ({ label: `${g.l} (${g.pct > 0 ? '+' : ''}${g.pct} %)`, value: v(app.bilan?.why?.[g.l]) })) },
    { n: '5', title: 'Attestation', rows: [{ label: 'Signature', value: null, note: `à signer par ${v(p?.president) ?? 'le représentant légal'}` }] },
  ];
  if (!sections[3]!.rows.length) sections[3]!.rows.push({ label: 'Écarts de plus de 20 %', value: 'aucun' });
  return { sections, ...countRows(sections) };
}

function countRows(sections: CerfaSection[]) {
  const rows = sections.flatMap(s => [...s.rows, ...(s.lines ?? []).flatMap(l => l.rows)]).filter(r => !/^Signature$/.test(r.label));
  return { filled: rows.filter(r => r.value !== null).length, missing: rows.filter(r => r.value === null).length };
}
